'use client';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useTheme, styled } from '@mui/material';

/**
 * Animation Definitions using MUI's styled API
 *
 * Three distinct animations create a polished loading experience:
 * 1. Fade In: Smooth appearance to avoid jarring transitions
 * 2. Spin: Continuous rotation indicating active loading
 * 3. Pulse: Subtle scaling for visual interest and "breathing" effect
 */

/**
 * ERA² Spinner Container Component
 *
 * Creates a full-overlay loading indicator with:
 * - Absolute positioning to cover parent container
 * - Flexbox centering for perfect alignment
 * - Smooth fade-in animation for polished UX
 */
const SpinnerContainer = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  animation: fadeIn 0.5s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

/**
 * ERA² Spinner SVG Component
 *
 * Applies multiple animations for engaging visual feedback:
 * - Primary spin animation (4s linear infinite)
 * - Secondary pulse animation (500ms ease infinite alternate)
 * - Fixed size for consistent appearance
 */
const SpinnerSVG = styled('svg')`
  width: 60px;
  height: 60px;
  animation: spin 4s linear infinite, pulse 500ms ease infinite alternate;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    from {
      scale: 100%;
    }
    to {
      scale: 85%;
    }
  }
`;

/**
 * Loading Text Component
 *
 * Styles for the optional loading text below the spinner:
 * - Consistent spacing from spinner
 * - Inter font family for modern appearance
 * - Theme-aware text color
 */
const LoadingText = styled('span')`
  margin-top: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
`;

/**
 * ERA² Spinner Component
 *
 * A sophisticated loading indicator featuring a custom SVG spinner with smooth
 * animations and theme integration. Designed to provide clear visual feedback
 * during async operations while maintaining consistent branding and accessibility.
 *
 * Key Features:
 * - Custom SVG spinner with unique design (partially filled circle pattern)
 * - Multi-layered animations: fade-in, rotation, and pulse effects
 * - Full MUI theme integration for colors and consistency
 * - Customizable loading text with theme-aware styling
 * - Absolute positioning for overlay usage
 * - Performance optimized with CSS animations (GPU accelerated)
 * - Accessibility considerations with proper contrast and motion
 * - Responsive design that works across all screen sizes
 * - Modern CSS features with fallbacks
 *
 * Design Philosophy:
 * - Non-intrusive: Overlays content without disrupting layout
 * - Informative: Clear visual indication of loading state
 * - Branded: Uses theme colors for consistent brand identity
 * - Smooth: Gentle animations that feel natural and polished
 * - Accessible: Respects user preferences and accessibility needs
 *
 * Animation Strategy:
 * - Fade-in prevents jarring appearance when loading starts
 * - Continuous rotation indicates active processing
 * - Subtle pulse adds visual interest without distraction
 * - 4-second rotation cycle provides smooth, unhurried feel
 * - All animations use transform/opacity for optimal performance
 *
 * Common Use Cases:
 * - API call loading states in forms and data fetching
 * - File upload progress indication
 * - Page transition loading overlays
 * - Component-level loading states in complex UIs
 * - Dashboard data refresh indicators
 * - Modal dialog loading states
 * - Async operation feedback in admin interfaces
 * - E-commerce cart and checkout processing
 *
 * Positioning Strategy:
 * - Uses absolute positioning with inset: 0 for full container overlay
 * - Parent container should have position: relative for proper containment
 * - Centers content using flexbox for reliable cross-browser alignment
 * - Works within any sized container from small components to full screen
 *
 * Performance Considerations:
 * - CSS animations run on GPU for smooth 60fps performance
 * - Minimal DOM structure reduces rendering overhead
 * - Memoized component prevents unnecessary re-renders
 * - SVG format ensures crisp rendering at any scale
 * - No external dependencies beyond MUI theme
 *
 * @param {Object} props - Component props
 * @param {string} [props.text="Loading..."] - Loading text to display below spinner
 * @returns {JSX.Element} Animated loading spinner with optional text
 *
 * @example
 * // Basic usage with default text
 * <div style={{ position: 'relative', height: '200px' }}>
 *   <Spinner />
 * </div>
 *
 * @example
 * // Custom loading message
 * <div style={{ position: 'relative', height: '100px' }}>
 *   <Spinner text="Uploading file..." />
 * </div>
 *
 * @example
 * // No text, spinner only
 * <div style={{ position: 'relative', height: '80px' }}>
 *   <Spinner text="" />
 * </div>
 *
 * @example
 * // Full page loading overlay
 * <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.8)' }}>
 *   <Spinner text="Loading application..." />
 * </div>
 */
const Spinner = ({ text = 'Loading...' }) => {
  // Access MUI theme for consistent colors and styling
  const theme = useTheme();

  return (
    <SpinnerContainer>
      {/* 
        Custom SVG Spinner Icon
        
        Design: Partial circle segments creating a dynamic loading appearance
        The SVG is split into two path groups:
        1. Static segments (grey) - provide visual context and structure
        2. Primary segments (theme primary color) - create the "active" loading indication
        
        This two-tone approach creates visual interest while maintaining clarity
        about what's "loaded" (primary color) vs what's pending (grey).
        
        ViewBox: 0 0 512 512 - Standard icon coordinate system
        Size: 60x60px - Optimal size for visibility without being intrusive
      */}
      <SpinnerSVG
        xmlns="http://www.w3.org/2000/svg"
        height="60"
        width="60"
        viewBox="0 0 512 512"
        role="img" // Accessibility: Mark as image for screen readers
        aria-label="Loading spinner" // Accessibility: Describe purpose
      >
        {/* 
          Background/Static Segments (Grey)
          These segments remain stationary and provide visual context.
          Using theme.palette.grey[100] ensures proper contrast in both light/dark themes.
        */}
        <path
          fill={theme.palette.grey[100]}
          d="M346.5 120.2c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L437 120.2c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-45.3 45.3zM384 256c0 17.7 14.3 32 32 32h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H416c-17.7 0-32 14.3-32 32zM256 384c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32s32-14.3 32-32V416c0-17.7-14.3-32-32-32zm135.8-37.5c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L391.8 437c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-45.3-45.3zM75 437c12.5 12.5 32.8 12.5 45.3 0l45.3-45.3c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L75 391.8c-12.5 12.5-12.5 32.8 0 45.3z"
        />

        {/* 
          Active/Primary Segments (Theme Primary Color)
          These segments use the theme's primary color to create brand consistency.
          The rotation animation makes these segments appear to "chase" around the circle.
        */}
        <path
          fill={theme.palette.primary.main}
          d="M256 0c-17.7 0-32 14.3-32 32V96c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32zM0 256c0 17.7 14.3 32 32 32H96c17.7 0 32-14.3 32-32s-14.3-32-32-32H32c-17.7 0-32 14.3-32 32zM120.2 75C107.7 62.5 87.5 62.5 75 75s-12.5 32.8 0 45.3l45.3 45.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L120.2 75z"
        />
      </SpinnerSVG>

      {/* 
        Optional Loading Text
        Displayed only when text prop is provided and not empty.
        Uses theme text color for proper contrast in light/dark modes.
      */}
      {text && <LoadingText style={{ color: theme.palette.text.primary }}>{text}</LoadingText>}
    </SpinnerContainer>
  );
};

/*
PROP DOCUMENTATION:
==================

text - String (Optional):
  Loading message to display below the spinner.
  
  Default: "Loading..."
  
  Common Usage Patterns:
  - Generic: "Loading...", "Please wait..."
  - Specific: "Uploading file...", "Saving changes...", "Fetching data..."
  - Process-specific: "Authenticating...", "Processing payment...", "Generating report..."
  - Empty string: "" (shows spinner only, no text)
  
  Accessibility Considerations:
  - Should be descriptive of the actual loading process when possible
  - Keep concise for screen reader users
  - Use ellipsis (...) to indicate ongoing process
  - Avoid technical jargon that users might not understand
  
  Internationalization:
  - Text should be translatable for multi-language applications
  - Consider text length variations in different languages
  - Some languages may require more/less space than English
  
  Performance Notes:
  - Text changes trigger re-renders, keep updates minimal
  - For frequently changing text, consider debouncing updates
  - Long text may affect layout, test with realistic content

USAGE GUIDELINES:
================

Container Requirements:
- Parent element should have position: relative, absolute, or fixed
- Parent should have defined dimensions (height/width) for proper centering
- For full-page overlays, use fixed positioning with inset: 0

Animation Performance:
- All animations use transform and opacity for GPU acceleration
- Avoid adding additional CSS transforms that might conflict
- Consider prefers-reduced-motion media query for accessibility

Theme Integration:
- Automatically uses theme.palette.primary.main for active segments
- Uses theme.palette.grey[100] for inactive segments  
- Text color adapts to theme.palette.text.primary
- Works seamlessly with light/dark theme switching

Responsive Behavior:
- Fixed 60px size works well across devices
- Consider larger sizes (80px-100px) for touch interfaces
- Text remains readable at default 16px across screen sizes
- SVG scales cleanly for high-DPI displays

Accessibility Features:
- SVG includes role="img" and aria-label for screen readers
- Color contrast meets WCAG guidelines in standard themes
- Animation timing designed to be non-disorienting
- Text provides context for the loading state

Browser Support:
- CSS Grid/Flexbox: IE11+ (with fallbacks)
- CSS Animations: All modern browsers
- Inset property: Chrome 87+, Firefox 66+, Safari 14.1+
- Fallback: Use top/right/bottom/left for older browsers
*/

// Enhanced PropTypes with comprehensive descriptions
Spinner.propTypes = {
  /**
   * Loading text to display below the spinner. Use empty string to hide text.
   * Should be descriptive and user-friendly. Defaults to "Loading..."
   */
  text: PropTypes.string,
};

// Memoization prevents unnecessary re-renders when parent components update
// but spinner props remain the same, improving performance
export default memo(Spinner);

// Display name for React DevTools debugging and component identification
Spinner.displayName = 'ERA² Spinner';
